import { $byId } from "@de-xima/xima-common-js-dom";
import { notNullish, notEmpty, parseString } from "@de-xima/xima-common-js-lang";
import jQuery from "jquery";

import { runRemoteCommand } from "../core/Util.ts";

/**
 * @param iframe Iframe to access.
 * @returns The href of the iframe location, or empty string when not allowed (cross-origin)
 */
function accessIframeLocation(iframe: HTMLIFrameElement): string {
  try {
    return iframe.contentWindow?.location.href ?? "";
  } catch {
    return "";
  }
}

/**
 * @param url Url to process.
 * @returns The url without the hash and query parameters.
 */
function getPlainUrl(url: string | undefined): string {
  if (notEmpty(url)) {
    const parser = document.createElement("a");
    parser.href = url;
    parser.hash = "";
    parser.search = "";
    return parser.href;
  }

  return "";
}

function toggleScrollers() {
  const tabMenu = document.querySelector(".tab-menu");
  if (notNullish(tabMenu)) {
    const hasOverflow = tabMenu.scrollWidth > tabMenu.clientWidth;
    if (hasOverflow) {
      jQuery(".tab-menu__scroll").removeClass("tab-menu__scoll--underflow");
    } else {
      jQuery(".tab-menu__scroll").addClass("tab-menu__scoll--underflow");
    }
  }
}

/**
 * Callback when a tab in the top menu of the designer was clicked. The tab menu
 * bar is updated via JSF, but until that update completes, this function
 * adjusts the style classes so that the clicked tab becomes visible
 * immediately.
 * @param cfg AJAX configuration from the request.
 * @returns Whether to proceed with the request.
 */
export const onTabClicked: PrimeFaces.ajax.CallbackOnstart = (cfg) => {
  const source = cfg.source;
  const sourceElement = typeof source === "string" ? $byId(source) : jQuery(source);
  const clickedTab = sourceElement.closest(".tab-menu__item");
  const clickedComponentId = parseString(clickedTab.data("componentId"));
  const otherTabs = clickedTab.siblings(".tab-menu__item");

  const clickedTabView = jQuery(".tab-view__item").filter((_$, el) => el.dataset.componentId === clickedComponentId);
  const otherTabViews = clickedTabView.siblings(".tab-view__item");

  // Do nothing when no clicked tab exists or the tab was not loaded yet
  if (clickedTab.length === 0 || clickedTabView.length === 0 || clickedTabView.children().length === 0) {
    return true;
  }

  clickedTab.addClass("tab-menu__item--active");
  clickedTab.removeClass("tab-menu__item--inactive");

  otherTabs.addClass("tab-menu__item--inactive");
  otherTabs.removeClass("tab-menu__item--active");

  otherTabViews.removeClass("tab-view__item--active");
  otherTabViews.addClass("tab-view__item--inactive");

  clickedTabView.addClass("tab-view__item--active");
  clickedTabView.removeClass("tab-view__item--inactive");

  return true;
};

/**
 * Sets up the resize observer for toggling the scroll left / right icons.
 */
export function setupScrollers(): void {
  const tabMenu = document.querySelector(".tab-menu");
  if (
    tabMenu instanceof HTMLElement &&
    typeof window.ResizeObserver === "function" &&
    tabMenu.dataset.scrollObserver !== "true"
  ) {
    const observer = new ResizeObserver(toggleScrollers);
    observer.observe(tabMenu);
    tabMenu.dataset.scrollObserver = "true";
    toggleScrollers();
  }
}

/**
 * @param iframe Iframe where the onload event occurred.
 */
export async function onIframeLoad(iframe: HTMLIFrameElement): Promise<void> {
  const currentUrl = accessIframeLocation(iframe);
  const targetUrl = parseString(jQuery(iframe).data("targetUrl"));
  if (getPlainUrl(currentUrl) !== getPlainUrl(targetUrl)) {
    const pageId = parseString(jQuery(iframe).data("pageId"));
    try {
      await runRemoteCommand(BackendCommand?.Designer?.onPageError, [{ name: "pageId", value: pageId }]);
    } catch (e) {
      console.error("Could not send process tab error", e);
    }
  }
}

/**
 * Reloads the iframe tab with the given ID.
 * @param pageId Page (tab) ID of the iframe.
 */
export function reloadIframe(pageId: string): void {
  const iframes = jQuery(".tab-view__iframe")
    .toArray()
    .filter((iframe) => iframe.dataset.pageId === pageId);
  if (iframes.length === 1) {
    const iframe = iframes[0];
    if (iframe instanceof HTMLIFrameElement) {
      iframe.src = parseString(iframe.dataset.targetUrl);
    }
  }
}

export function onDocumentReady(): void {
  jQuery(window).on("beforeunload", () => {
    if (jQuery(".tab-menu .tab-menu__close--unsaved").length > 0) {
      return "Unsaved changes";
    }
    return undefined;
  });
}
